import React, { useState } from 'react';
import axios from 'axios';
import './UserCheck.css';

const SECTION_ICONS = {
    user: '👤',
    messages: '💬',
    cheat: '⚠️',
    identifier: '🔗',
    tickets: '🎫'
};

const UserCheck = () => {
    const [query, setQuery] = useState('');
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openServers, setOpenServers] = useState({});
    const [openSections, setOpenSections] = useState({});

    const toggleServer = (id) => {
        setOpenServers((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSection = (key) => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const API_BASE = process.env.REACT_APP_API_BASE;

    const handleCheck = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;

        setLoading(true);
        setError('');
        setResponse(null);

        try {
            const token = localStorage.getItem('discord_token');
            const res = await axios.post(
                `${API_BASE}/api/user-check`,
                { query: query.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setResponse(res.data);
        } catch (err) {
            console.error('Fehler beim User-Check:', err);
            setError(
                err.response?.data?.error ||
                err.response?.data?.details ||
                err.message ||
                'Ein unbekannter Fehler ist aufgetreten.'
            );
        } finally {
            setLoading(false);
        }
    };

    const isTimeKey = (key) =>
        /(time|date|at|since|until|expire|created|updated|joined|timestamp|birth)/i.test(key || '');

    const isIdKey = (key) =>
        /(id|_id|snowflake|discord|userid|user_id|guild|channel|message|role|member)/i.test(key || '');

    const formatTimestamp = (value) => {
        let ms = null;
        if (typeof value === 'number') {
            ms = value.toString().length > 11 ? value : value * 1000;
        } else if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
            const n = Number(value);
            ms = value.trim().length > 11 ? n : n * 1000;
        } else if (typeof value === 'string') {
            const parsed = Date.parse(value);
            if (!isNaN(parsed)) ms = parsed;
        }
        if (ms === null || isNaN(ms)) return null;
        const d = new Date(ms);
        if (isNaN(d.getTime())) return null;
        const date = d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
        const time = d.toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin' });
        return { date, time, iso: d.toISOString() };
    };

    const formatScalar = (value, key, parent) => {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'boolean') return value ? '✅ Ja' : '❌ Nein';
        if (value === '' ) return '—';
        if (typeof value === 'object') return '';

        if (isTimeKey(key)) {
            const t = formatTimestamp(value);
            if (t) {
                return (
                    <span className="uc-time" title={t.iso}>
                        🕒 {t.date} – {t.time} Uhr
                    </span>
                );
            }
        }

        if (isIdKey(key) && typeof value === 'string' && /^\d{10,}$/.test(value.trim())) {
            if (parent) {
                const username = parent.username || parent.name || parent.displayName || '';
                const tag = parent.discriminator || parent.tag || '';
                const parts = [username, `Discord ID: ${value}`, tag].filter(Boolean);
                if (parts.length > 1) {
                    return <span className="uc-id">{parts.join(' - ')}</span>;
                }
            }
            return <span className="uc-id">{value}</span>;
        }

        return String(value);
    };

    const renderEntries = (data, depth = 0) => {
        if (data === null || data === undefined) return <span className="uc-empty">Keine Daten</span>;
        if (typeof data !== 'object') {
            return <div className="uc-scalar">{formatScalar(data, '_', null)}</div>;
        }

        if (Array.isArray(data)) {
            if (data.length === 0) return <span className="uc-empty">Keine Einträge</span>;
            return (
                <div className="uc-servers">
                    {data.map((item, i) => renderDropdown(item, i, depth))}
                </div>
            );
        }

        const entries = Object.entries(data).filter(([, v]) => v !== undefined);
        if (entries.length === 0) return <span className="uc-empty">Keine Daten</span>;

        return (
            <div className={`uc-grid depth-${depth}`}>
                {entries.map(([key, value]) => {
                    const isComplex = value !== null && typeof value === 'object';
                    return (
                        <div className={`uc-field${isComplex ? ' complex' : ''}`} key={key}>
                            <div className="uc-field-key">{getFieldLabel(key)}</div>
                            {isComplex ? (
                                <div className="uc-field-value complex">
                                    {renderEntries(value, depth + 1)}
                                </div>
                            ) : (
                                <div className="uc-field-value">{formatScalar(value, key, data)}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const getFieldLabel = (key) => {
        const map = {
            _id: 'User Info',
            id: 'ID',
            username: 'Username',
            displayName: 'Anzeigename',
            discriminator: 'Discord Tag',
            tag: 'Tag',
            name: 'Name',
            createdAt: 'Erstellt am',
            updatedAt: 'Aktualisiert am',
            joinedAt: 'Beigetreten am',
            message: 'Nachricht',
            content: 'Inhalt',
            channel: 'Kanal',
            server: 'Server',
            guild: 'Gilde',
            role: 'Rolle',
            permission: 'Berechtigung',
            flag: 'Flag',
            reason: 'Grund',
            duration: 'Dauer',
            expiresAt: 'Läuft ab',
            bannedAt: 'Gebannt am',
            unbannedAt: 'Entbannt am',
            kick: 'Kick',
            ban: 'Ban',
            mute: 'Mute',
            warn: 'Verwarnung'
        };
        return map[key] || key;
    };

    const getUserDisplayName = (data) => {
        if (!data || typeof data !== 'object') return null;
        if (Array.isArray(data)) {
            const sample = data[0];
            if (sample && typeof sample === 'object') {
                return getUserDisplayName(sample);
            }
            return null;
        }
        return (
            data.username ||
            data.displayName ||
            data.display_name ||
            data.name ||
            null
        );
    };

    const getSectionCount = (data) => {
        if (!data) return 0;
        if (Array.isArray(data)) return data.length;
        if (typeof data === 'object') {
            let count = 0;
            for (const value of Object.values(data)) {
                if (Array.isArray(value)) count += value.length;
                else if (value !== null && typeof value === 'object') count++;
            }
            return count;
        }
        return 1;
    };

    const renderDropdown = (item, index, depth) => {
        const openKey = `arr-${depth}-${index}`;
        const open = !!openServers[openKey];
        const label = getItemLabel(item);
        const countLabel = getItemCountLabel(item);

        return (
            <div className="uc-server" key={index}>
                <button
                    type="button"
                    className="uc-server-toggle"
                    onClick={() => toggleServer(openKey)}
                    aria-expanded={open}
                >
                    <span className="uc-server-caret">{open ? '▼' : '▶'}</span>
                    <span className="uc-server-name">{label}</span>
                    {countLabel && <span className="uc-server-count">{countLabel}</span>}
                </button>
                {open && (
                    <div className="uc-server-body">
                        {typeof item === 'object' && item !== null
                            ? renderEntries(item, depth + 1)
                             : <div className="uc-scalar">{formatScalar(item, '_', null)}</div>}
                    </div>
                )}
            </div>
        );
    };

    const getItemLabel = (item) => {
        if (!item || typeof item !== 'object') return String(item ?? 'Eintrag');
        return (
            item.name ||
            item.username ||
            item.displayName ||
            item.display_name ||
            item.title ||
            item.id ||
            item._id ||
            'Eintrag'
        );
    };

    const getItemCountLabel = (item) => {
        if (!item || typeof item !== 'object') return '';
        const arrays = ['data', 'tickets', 'messages', 'members', 'roles', 'entries', 'items'];
        for (const key of arrays) {
            if (Array.isArray(item[key])) {
                const n = item[key].length;
                return `${n} ${n === 1 ? 'Rolle' : 'Rollen'}`;
            }
        }
        return '';
    };


    const DROPDOWN_SECTIONS = new Set(['messages', 'cheat', 'identifier', 'tickets']);

    const sections = response?.sections || {};

    return (
        <div className="dashboard-main">
            <div className="user-check-container">
                <div className="user-check-card">
                    <div className="user-check-header">
                        <div className="user-check-header-icon">
                            <span role="img" aria-label="check">🔎</span>
                        </div>
                        <h2>Analystic Network – User Check</h2>
                    </div>

                    <p className="user-check-subtitle">
                        Vollständige Prüfung einer Discord-ID über alle Analystic-Endpunkte.
                    </p>

                    <form className="user-check-form" onSubmit={handleCheck}>
                        <input
                            type="text"
                            className="user-check-input"
                            placeholder="Discord-ID (z.B. 123456789012345678)"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="user-check-btn"
                            disabled={loading || !query.trim()}
                        >
                            {loading ? '🔎 Prüfe...' : '🔎 Prüfen'}
                        </button>
                    </form>

                    {error && (
                        <div className="user-check-message error">
                            ❌ {error}
                        </div>
                    )}

                    {response && (
                        <div className="user-check-result">
                            {/* Gesamt-Status */}
                            <div className={`user-check-overall ${response.found ? 'danger' : 'success'}`}>
                                {response.found
                                    ? `⚠️ Einträge gefunden für ${response.query}`
                                    : `✅ Keine Treffer für ${response.query}`}
                            </div>

                            {/* Sektionen als Grid */}
                            <div className="user-check-sections">
                                {Object.entries(sections).map(([key, sec]) => (
                                    <div className="uc-section" key={key}>
                                        <div className="uc-section-head">
                                            <span className="uc-section-icon">{SECTION_ICONS[key] || '•'}</span>
                                            <span className="uc-section-label">{sec.label}</span>
                                            {key === 'user' && getUserDisplayName(sec.data) && (
                                                <span className="uc-user-display-name">{getUserDisplayName(sec.data)}</span>
                                            )}
                                            {sec.notFound
                                                ? <span className="uc-section-flag none">0 Treffer</span>
                                                : sec.ok
                                                    ? <span className="uc-section-flag ok">{getSectionCount(sec.data)} Treffer</span>
                                                    : <span className="uc-section-flag err">Fehler</span>}
                                        </div>
                                        <div className="uc-section-data">
                                            {DROPDOWN_SECTIONS.has(key) ? (
                                                <div className="uc-server">
                                                    <button
                                                        type="button"
                                                        className="uc-server-toggle"
                                                        onClick={() => toggleSection(key)}
                                                        aria-expanded={!!openSections[key]}
                                                    >
                                                        <span className="uc-server-caret">{openSections[key] ? '▼' : '▶'}</span>
                                                        <span className="uc-server-name">{sec.label}</span>
                                                        <span className="uc-server-count">Details anzeigen</span>
                                                    </button>
                                                    {openSections[key] && (
                                                        <div className="uc-server-body">
                                                            {renderEntries(sec.data)}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                renderEntries(sec.data)
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserCheck;
